import compareVersions from 'compare-versions';

const DEFAULT_VERSION = '0.0.0';

/**
 * 只保留 major.minor.patch 三段，后面 build 等丢弃
 * @param ver 版本号
 * @returns 三段 major.minor.patch
 */
const formatWeixinVer = (ver: string): string => ver.split('.').slice(0, 3)
  .join('.');

// @ts-ignore
export const getUserAgent = () => navigator.wxuserAgent || navigator.userAgent;

/**
 * 获取OS信息
 */
export const getOSInfoObj = (): {
  type: 'iphone' | 'android' | 'harmony' | 'none';
  version: string;
} => {
  const ua = getUserAgent();
  let pattern = /(iPhone OS |iPad; CPU OS )([\d_]+)/i;
  let wc = ua.match(pattern);
  if (wc?.[2]) {
    return { type: 'iphone', version: wc[2].replace(/_/g, '.') };
  }
  pattern = /Android ([\d.]+)/i;
  wc = ua.match(pattern);
  if (wc?.[1]) {
    return { type: 'android', version: wc[1] };
  }
  pattern = /OpenHarmony[\s-]+([\d.]+)/i;
  wc = ua.match(pattern);
  if (wc?.[1]) {
    return { type: 'harmony', version: wc[1] };
  }
  return { type: 'none', version: DEFAULT_VERSION };
};

/** 获取系统信息文本 */
export const getOSInfoText = () => {
  const os = getOSInfoObj();
  return `${os.type} ${os.version}`;
};

/**
 * 获取微信版本（仅取 UA 中 MicroMessenger/ 与左括号之间的点分号，括号内忽略；统一为三段 major.minor.patch）
 */
export const getWeixinVer = (): string => {
  const raw = getUserAgent().match(/MicroMessenger\/([\d.]+)/i)?.[1] || DEFAULT_VERSION;
  return formatWeixinVer(raw);
};

/**
 * 比较版本1和版本2的高低, 0 相等，-1 v1 < v2， 1 v1 > v2
 * @param v1 版本1(格式类似7.0.12)
 * @param v2 版本2(格式类似7.0.12)
 */
export const verCompare = (v1: string, v2: string) => {
  // 兜底处理，如果没有传v1或v2，则默认是0
  const formatV1 = v1 || '0';
  const formatV2 = v2 || '0';
  return compareVersions(formatV1, formatV2);
};

/**
 * 比较微信版本大于等于ver
 * @param ver 比较的版本
 */
export const isWexinVerLargeOrEqualThan = (ver: string) => {
  const curVer = getWeixinVer();
  return verCompare(curVer, ver) > -1;
};

/**
 * 判断安卓、ios、鸿蒙的版本是否大于等于指定版本
 * 如果对某系统没版本要求，需显式传入如 '0.0.0'；未传当前平台字段则视为不满足
 * @param minVer 各系统的最低版本
 */
export const isOSVerLargeOrEqualThan = (minVer: {
  android?: string;
  iphone?: string;
  harmony?: string;
}) => {
  const { type, version } = getOSInfoObj();
  if (type === 'none') {
    return false;
  }
  const ver = minVer[type];
  if (ver === undefined || ver === null || ver === '') {
    return false;
  }
  return verCompare(version, ver) > -1;
};

/**
 * 判断在所有的系统下最小要求支持的微信版本
 * @param minVersions 微信最小版本号（需要指定每个系统下的版本号，如：iphone: "8.0.20"，android:"8.0.21",如果对版本没要求，可传入0.0.0）
 * @returns
 */
export const isAllWxVersionSupported = (minVersions: {
  iphone?: string;
  android?: string;
  harmony?: string;
}) => {
  const { type } = getOSInfoObj();
  if (type === 'none') {
    return false;
  }
  return isWexinVerLargeOrEqualThan(minVersions[type] || '0.0.0');
};

/** 判断是否在微信浏览器内 */
export const isInWeixin = () => /MicroMessenger/i.test(getUserAgent());

/**
 * 判断是否在小程序内
 */
// @ts-ignore
export const isInMiniProgram = () => /miniprogram/i.test(getUserAgent()) || window.__wxjs_environment === 'miniprogram';

/**
 * H5 被小程序内嵌时，获取宿主小程序appId
 */
export const getMiniProgramAppId = () => {
  const ua = getUserAgent();
  const pattern = /miniProgram\/(\w+)/;
  const result = pattern.exec(ua);
  if (result?.[1]) {
    return result[1];
  }
  return '';
};

/**
 * 将微信十六进制版本转换为数字版本（均为三段 major.minor.patch）
 *
 * iOS/Android：去掉 `0x` 与平台半字节后按 1+2+2 个十六进制位解析。
 * 鸿蒙：`0x` 后以 `f3` 为平台前缀时，其后 6 个十六进制位按 1+1+2 解析。
 *
 * @param hexVer 十六进制版本字符串，如 "0x18003D00"
 * @returns 可读版本；非法输入返回 `0.0.0`
 */
export const getWeixinVerFromHex = (hexVer: string): string => {
  if (!hexVer || !/^0x[0-9A-Fa-f]+$/i.test(hexVer)) {
    return DEFAULT_VERSION;
  }
  const hexVersionStr = hexVer.slice(2);
  const ohosRegex = /^f3([0-9a-f])([0-9a-f])([0-9a-f]{2})([0-9a-f]{2})$/i;
  const regex = /([0-9a-f])([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
  const ms = hexVersionStr.match(ohosRegex) || hexVersionStr.match(regex);
  if (ms) {
    const result = ms
      .filter((_, index) => index > 0)
      .map(item => parseInt(item, 16))
      .join('.');
    return formatWeixinVer(result);
  }
  return DEFAULT_VERSION;
};
